import * as THREE from "three";
import { useRef, useState, Suspense } from "react";
import { useRoute, useLocation } from "wouter";
import { useFrame, useLoader } from "@react-three/fiber";
import { easing } from "maath";
import {
  useCursor,
  MeshPortalMaterial,
  Text,
  useGLTF,
} from "@react-three/drei";

function CompanyLogo({ company_img, ...props }) {
  const texture = useLoader(THREE.TextureLoader, company_img);
  texture.center.set(0.5, 0.5); // Set origin to center
  texture.rotation = Math.PI; // Rotate 90 degrees (in radians)
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.x = -1;
  texture.center.x = 0.5;

  return (
    <mesh castShadow {...props}>
      <roundedPlaneGeometry args={[0.9, 0.9, 0.1]} />
      <meshStandardMaterial map={texture} side={THREE.DoubleSide} />
    </mesh>
  );
}

export default function Frame({
  id,
  date = "something temporary",
  icon = "none",
  jTitle,
  company_name,
  company_website,
  company_img,
  job_description,
  bg,
  width = 9,
  months = 12.0,
  height = 1.9,
  children,
  ...props
}) {
  const portal = useRef();
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/item/:id");
  const [hovered, hover] = useState(false);

  useCursor(hovered);
  useFrame((state, dt) =>
    easing.damp(portal.current, "blend", params?.id === id ? 1 : 0, 0.2, dt),
  );

  const handleClick = () => {
    window.open(company_website, "_blank", "noopener,noreferrer");
  };

  return (
    <group {...props} rotation={[-Math.PI / 2, 0, 0]}>
      <Text
        // font={suspend(medium).default}
        color={"black"}
        fontSize={0.2}
        anchorY="top"
        anchorX="left"
        lineHeight={0.3}
        position={[-7, height / 2 - 0.1, 0.01]}
        material-toneMapped={false}
      >
        {date}
      </Text>
      <Text
        // font={suspend(regular).default}
        color={"black"}
        fontSize={0.35}
        anchorY="top"
        anchorX="left"
        position={[-3.7, height / 2, 0.01]}
        material-toneMapped={false}
      >
        {company_name}
      </Text>
      <Text
        // font={suspend(medium).default}
        color={"black"}
        fontSize={0.2}
        anchorY="top"
        anchorX="left"
        lineHeight={1.1}
        position={[-3.7, height / 2 - 0.5, 0.01]}
        material-toneMapped={false}
      >
        {jTitle}
      </Text>

      <Text
        // font={suspend(regular).default}
        color={"black"}
        fontSize={0.15}
        anchorY="bottom"
        anchorX="left"
        position={[width / 2 + 1, 0, 0.04]}
        material-toneMapped={false}
        maxWidth={5}
      >
        {job_description}
      </Text>
      <mesh
        name={id}
        // onDoubleClick={(e) => (
        //   e.stopPropagation(), setLocation("/item/" + e.object.name)
        // )}
        onPointerOver={(e) => hover(true)}
        onPointerOut={() => hover(false)}
        position={[0.5, 0, 0]}
        receiveShadow
        castShadow
      >
        <roundedPlaneGeometry args={[width, height, 0.1]} />
        <MeshPortalMaterial
          ref={portal}
          events={params?.id === id}
          side={THREE.DoubleSide}
        >
          <color attach="background" args={[bg]} />
          {children}
        </MeshPortalMaterial>
      </mesh>
      <mesh castShadow position={[0.5, 0, -0.02]} rotation={[Math.PI, 0, 0]}>
        <roundedPlaneGeometry args={[width, height, 0.1]} />

        <meshStandardMaterial transparent opacity={0} side={THREE.DoubleSide} />
      </mesh>

      <Suspense
        fallback={
          <mesh
            castShadow
            position={[-4.5, height / 2 - 0.45, 0.02]}
            rotation={[Math.PI, 0, 0]}
          >
            <roundedPlaneGeometry args={[0.9, 0.9, 0.1]} />
            <meshStandardMaterial color="#eeeeee" side={THREE.DoubleSide} />
          </mesh>
        }
      >
        <CompanyLogo
          company_img={company_img}
          position={[-4.5, height / 2 - 0.45, 0.02]}
          rotation={[Math.PI, 0, 0]}
          onPointerOver={(e) => {
            e.stopPropagation();
            hover(true);
          }}
          onPointerOut={() => hover(false)}
          onClick={handleClick}
        />
      </Suspense>
    </group>
  );
}

import model01 from "./models/01.glb";
import image01 from "./models/01.png";

import model02 from "./models/02.glb";
import image02 from "./models/02.jpg";

import model03 from "./models/03.glb";
import image03 from "./models/03.jpg";

import model04 from "./models/04.glb";
import image04 from "./models/04.jpg";

import model05 from "./models/05.glb";
import image05 from "./models/05.jpg";

import model06 from "./models/06.glb";
import image06 from "./models/06.jpg";

export const frames = [
  {
    id: "01",
    date: "oct 2024 - jun 2025",
    jTitle: "Bim Technology Manager, \nAssociate Partner",
    company_name: "Foster + Partners",
    job_description:
      "software development/ internal workflows/ geometry + process automation",
    months: 9,
    bg: "white",
    model: {
      src: model01,
      scale: 0.05,
      position: [0, 0, -1],
    },
    company_website: "https://www.fosterandpartners.com/",
    company_img: image01,
  },
  {
    id: "02",
    date: "feb 2021 - sep 2024",
    jTitle: "Senior Geometry Engineer",
    company_name: "AiBuild",
    job_description:
      "contributed to, maintained and improved the core geometry engine, applied to large-scale additive manufacturing",
    months: 31,
    bg: "white",
    model: {
      src: model02,
      scale: 0.15,
      position: [-1, -1.5, -2],
    },
    company_website: "https://ai-build.com/",
    company_img: image02,
  },
  {
    id: "03",
    date: "nov 2020 - feb 2022",
    jTitle: "Facade Engineer",
    company_name: "Front",
    job_description:
      "automated geometry production and documentation for the manufacturing stage of complex high-rise curtain wall systems \nproject types: office",
    months: 16,
    bg: "white",
    model: {
      src: model03,
      scale: 0.3,
      position: [-2, -0.7, 0],
      rotation: [0, -0.8, 0],
    },
    company_website: "https://front.global/",
    company_img: image03,
  },
  {
    id: "04",
    date: "jan 2020 - nov 2020",
    jTitle: "Regional DT Manager",
    company_name: "Gensler",
    job_description:
      "worked on internal tools, improving operational efficiency; focused on design, visualization and automation \nproject types: masterplans and buildings",
    months: 9,
    bg: "white",
    model: {
      src: model04,
      scale: 0.2,
      position: [0, -1.5, 0],
    },
    company_website: "https://www.gensler.com/",
    company_img: image04,
  },
  {
    id: "05",
    date: "jun 2018 - dec 2019",
    jTitle: "Bim Designer",
    company_name: "Zaha Hadid Architects",
    job_description:
      "geometry automation focused on interoperability, rationalization, and design \nproject types: airports, masterplans and office/resi ",
    months: 18,
    bg: "white",
    model: {
      src: model05,
      scale: 0.1,
      position: [0, -0.5, 0],
      rotation: [0, 0, 0],
    },
    company_website: "https://www.zaha-hadid.com/",
    company_img: image05,
  },
  {
    id: "06",
    date: "oct 2014 - jul 2018",
    jTitle: "Computational Designer",
    company_name: "Arcadis",
    job_description:
      "developed internal tools, increasing operational efficiency and geometry automation \nproject types: infrastructure (highway/railway/bridges/tunnels)",
    months: 45,
    bg: "white",
    model: {
      src: model06,
      scale: 0.1,
      position: [0, -0.1, 0],
      rotation: [0, -0.8, 0],
    },
    company_website: "https://www.arcadis.com/",
    company_img: image06,
  },
];
